"use client";

import { Check, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils/cn";

export type StepTabItem = {
  id: string;
  label: string;
  summary: string;
  icon: LucideIcon;
  complete?: boolean;
};

type StepTabsProps = {
  items: StepTabItem[];
  activeId: string;
  onSelect: (id: string) => void;
  ariaLabel: string;
  className?: string;
  compact?: boolean;
  leading?: ReactNode;
  stepInfoOnTrailing?: boolean;
  hideProgressHeader?: boolean;
  trailing?: ReactNode;
};

export function StepTabs({ items, activeId, onSelect, ariaLabel, className, leading, stepInfoOnTrailing = false, hideProgressHeader = false, trailing }: StepTabsProps) {
  const activeIndex = Math.max(
    0,
    items.findIndex((item) => item.id === activeId),
  );
  const progressPct = items.length > 1 ? ((activeIndex + 1) / items.length) * 100 : 100;

  return (
    <nav aria-label={ariaLabel} className={cn("space-y-ds-3", className)}>
      {!hideProgressHeader ? <>
        <div className="flex items-center justify-between gap-ds-3 text-caption">
          {leading ?? (stepInfoOnTrailing ? <span className="text-muted">{items[activeIndex]?.label}</span> : <span className="font-medium text-foreground">Step {activeIndex + 1} of {items.length}</span>)}
          {trailing ?? (stepInfoOnTrailing ? <span className="font-medium text-foreground">Step {activeIndex + 1} of {items.length}</span> : <span className="text-muted">{items[activeIndex]?.label}</span>)}
        </div>
        <div className="h-1 overflow-hidden rounded-full bg-muted-bg" role="progressbar" aria-valuemin={1} aria-valuemax={items.length} aria-valuenow={activeIndex + 1} aria-label={`Wizard progress, step ${activeIndex + 1} of ${items.length}`}>
          <div className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out" style={{ width: `${progressPct}%` }} />
        </div>
      </> : null}

      <ol className="flex gap-ds-1 overflow-x-auto pb-ds-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((item, index) => {
          const active = item.id === activeId;
          const complete = item.complete && !active;
          const reachable = index <= activeIndex || complete;

          return (
            <li key={item.id} className="flex min-w-[4.5rem] flex-1 shrink-0 flex-col items-stretch">
              <button
                type="button"
                role="tab"
                aria-selected={active}
                aria-current={active ? "step" : undefined}
                title={item.summary}
                onClick={() => onSelect(item.id)}
                className={cn(
                  "group flex flex-col items-center gap-ds-1.5 rounded-control border border-transparent px-ds-1 py-ds-2 transition-colors",
                  active && "step-tab-active",
                  !active && reachable && "hover:bg-muted-bg/40",
                )}
              >
                <span
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-full border text-micro font-semibold tabular-nums transition-colors",
                    active && "step-tab-badge-active",
                    complete && "step-tab-badge-complete",
                    !active && !complete && "border-card-border bg-card text-muted group-hover:border-border group-hover:text-foreground",
                  )}
                >
                  {complete ? <Check className="h-3.5 w-3.5" aria-hidden /> : index + 1}
                </span>
                <span
                  className={cn(
                    "line-clamp-2 text-center text-micro leading-snug",
                    active ? "font-semibold text-foreground" : "font-medium text-muted",
                  )}
                >
                  {item.label}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
